import { useState } from 'react';

import {
  ChartCard,
  College,
  BarChartProps,
  SegmentedBarCard,
  parseISODate,
} from '@backrs_ui/common-ui';
import type { SegmentedBarRowData } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineIconItem } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineIconItem';
import {
  statRowMainSx,
  statRowSx,
  statRowStatsSx,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/statColumnLayout';

import { Box, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './WhoGetsIn.agentContext';
import {
  EXTRACURRICULAR_LEVELS,
  RIGOR_LEVELS,
  SELECTIVITY_LEVELS,
  getExtracurricularsLabel,
  getRigorLabel,
  getSelectivityLabel,
} from '../college.util';

const WHO_GETS_IN_STRINGS = appStrings.modules.college.whoGetsIn;

const REQUIREMENTS = [
  { key: 'essay_requirement', title: WHO_GETS_IN_STRINGS.essay, iconName: 'Pencil01' },
  {
    key: 'recommendation_requirement',
    title: WHO_GETS_IN_STRINGS.recommendations,
    iconName: 'Mail01',
  },
  { key: 'interview_requirement', title: WHO_GETS_IN_STRINGS.interview, iconName: 'Users01' },
] as const;

const formatFullDate = (date: string | Date) =>
  (typeof date === 'string' ? parseISODate(date.slice(0, 10)) : date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

type WhoGetsInProps = Readonly<{
  college: College;
}>;

export const WhoGetsIn = ({ college }: WhoGetsInProps) => {
  useAgentContext(college);

  const [isSATOrACT, setIsSATOrACT] = useState<'SAT' | 'ACT'>(college.actcm25 ? 'ACT' : 'SAT');

  const SATData: BarChartProps = {
    chartMin: 400,
    chartMax: 1600,
    ticks: [400, 800, 1200, 1600],
    dataMin: (college.satmt25 ?? 0) + (college.satvr25 ?? 0),
    dataAverage: (college.satmt50 ?? 0) + (college.satvr50 ?? 0),
    dataMax: (college.satmt75 ?? 0) + (college.satvr75 ?? 0),
  };

  const ACTData: BarChartProps = {
    chartMin: 1,
    chartMax: 36,
    ticks: [1, 9, 18, 27, 36],
    dataMin: college.actcm25 ?? 0,
    dataAverage: college.actcm50 ?? 0,
    dataMax: college.actcm75 ?? 0,
  };

  const GPAData: BarChartProps = {
    chartMin: 0,
    chartMax: 4.5,
    ticks: [0, 1, 2, 3, 4],
    dataMin: college.gpa_25 ?? 0,
    dataAverage: college.gpa_50 ?? 0,
    dataMax: college.gpa_75 ?? 0,
  };

  const academicProfileRows: SegmentedBarRowData[] = [
    college.selectivity != null && {
      title: WHO_GETS_IN_STRINGS.selectivity.title,
      options: SELECTIVITY_LEVELS,
      selectedValue: getSelectivityLabel(college.selectivity),
      infoVariant: { type: 'modal' as const, modalData: WHO_GETS_IN_STRINGS.selectivity.modal },
    },
    college.extracurricular_involvement != null && {
      title: WHO_GETS_IN_STRINGS.charts.extracurriculars.title,
      options: EXTRACURRICULAR_LEVELS,
      selectedValue: getExtracurricularsLabel(college.extracurricular_involvement),
      infoVariant: {
        type: 'modal' as const,
        modalData: WHO_GETS_IN_STRINGS.charts.extracurriculars.modal,
      },
    },
    college.coursework_rigor != null && {
      title: WHO_GETS_IN_STRINGS.charts.rigor.title,
      options: RIGOR_LEVELS,
      selectedValue: getRigorLabel(college.coursework_rigor),
      infoVariant: { type: 'modal' as const, modalData: WHO_GETS_IN_STRINGS.charts.rigor.modal },
    },
  ].filter(Boolean) as SegmentedBarRowData[];

  return (
    <Box>
      {/* SEGMENT CHART + STAT LINE ITEMS */}
      <Box sx={statRowSx}>
        {academicProfileRows.length > 0 && (
          <Box sx={statRowMainSx}>
            <SegmentedBarCard
              title={WHO_GETS_IN_STRINGS.segmentChartTitle}
              rows={academicProfileRows}
            />
          </Box>
        )}

        <Box sx={statRowStatsSx}>
          {college.admission_rate != null && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.charts.acceptanceRate.title}
              iconName="PieChart02"
              text={`${Math.round(college.admission_rate * 100)}%`}
            />
          )}

          {college.early_decision_deadline && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.earlyDecision}
              iconName="CalendarDate"
              text={formatFullDate(college.early_decision_deadline)}
            />
          )}

          {college.early_action_deadline && (
            <LineIconItem
              title={
                college.early_action_restrictive
                  ? WHO_GETS_IN_STRINGS.restrictiveEarlyAction
                  : WHO_GETS_IN_STRINGS.earlyAction
              }
              iconName="CalendarDate"
              text={formatFullDate(college.early_action_deadline)}
            />
          )}

          {college.application_deadline && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.applicationDeadline}
              iconName="CalendarDate"
              text={formatFullDate(college.application_deadline)}
            />
          )}

          {college.rolling_admission && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.rollingAdmission}
              iconName="Clock"
              text={WHO_GETS_IN_STRINGS.yes}
            />
          )}

          {college.notification_date && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.notificationDate}
              iconName="CalendarCheck01"
              text={formatFullDate(college.notification_date)}
            />
          )}

          {(college.test_blind || college.test_optional != null) && (
            <LineIconItem
              title={
                college.test_blind
                  ? WHO_GETS_IN_STRINGS.testBlind
                  : WHO_GETS_IN_STRINGS.testOptional
              }
              iconName="Edit05"
              text={
                college.test_blind || college.test_optional
                  ? WHO_GETS_IN_STRINGS.yes
                  : WHO_GETS_IN_STRINGS.no
              }
            />
          )}

          {(college.application_fee != null || college.application_fee_waiver) && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.applicationFee}
              iconName="CurrencyDollarCircle"
              text={
                college.application_fee != null
                  ? WHO_GETS_IN_STRINGS.applicationFeeValue(
                      college.application_fee,
                      Boolean(college.application_fee_waiver),
                    )
                  : WHO_GETS_IN_STRINGS.feeWaiverOnly
              }
            />
          )}

          {REQUIREMENTS.map(
            ({ key, title, iconName }) =>
              college[key] && (
                <LineIconItem
                  key={key}
                  title={title}
                  iconName={iconName}
                  text={WHO_GETS_IN_STRINGS.requirementLabels[college[key]]}
                />
              ),
          )}
        </Box>
      </Box>

      {/* HIGH SCHOOL GPA AND SAT SCORE */}
      <Box sx={chartRowContainerSx}>
        <ChartCard
          title={WHO_GETS_IN_STRINGS.charts.highSchoolGPA.title}
          additionalInfoVariant={{
            type: 'TOOLTIP',
            toolTipText: WHO_GETS_IN_STRINGS.charts.highSchoolGPA.tooltip,
          }}
          chartVariant={{
            type: 'BAR',
            bar: GPAData,
          }}
          overrideSx={{ container: chartCardNoMaxWidthSx }}
        />

        {/* inlining the conditionals here gets us a nicer animation when the type changes */}
        <ChartCard
          title={WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].title}
          additionalInfoVariant={{
            type: 'TOOLTIP',
            toolTipText:
              WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].tooltip,
          }}
          headerAction={{
            label:
              WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].switch,
            icon: 'RefreshCcw05',
            onClick: () => setIsSATOrACT((prev) => (prev === 'SAT' ? 'ACT' : 'SAT')),
          }}
          chartVariant={{
            type: 'BAR',
            bar: isSATOrACT === 'SAT' ? SATData : ACTData,
          }}
          overrideSx={{ container: chartCardNoMaxWidthSx }}
        />
      </Box>
    </Box>
  );
};

const chartRowContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: backrs.spacing['spacing-3xl'],
  gap: backrs.spacing['spacing-3xl'],
  flexWrap: 'wrap',
  '& > *': {
    flex: '1 1 100%',
    minWidth: 0,
  },
  '@container (min-width: 700px)': {
    '& > *': {
      flex: '1 1 0',
    },
  },
});

const chartCardNoMaxWidthSx: SxProps<Theme> = {
  maxWidth: 'none',
};
